import { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import Iconify from '../../../components/Iconify';
import { authApiCall } from '../../../services/http';
import { endpoints } from '../../../config';
import { PLAN_STATUS_OPTIONS } from 'src/helpers/subscriptions';

const TYPE_OPTIONS = [
  { value: 'plan', label: 'Plan' },
  { value: 'addon', label: 'Add-on' },
];

const EMPTY_FORM = {
  slug: '',
  type: 'plan',
  displayName: '',
  status: 'active',
  capabilities: [],
  limits: [],
  comments: '',
};

function limitsToEntries(obj) {
  if (!obj || typeof obj !== 'object') return [];
  return Object.entries(obj).map(([key, value]) => ({ key, value: String(value) }));
}

function entriesToLimits(entries) {
  const obj = {};
  entries.forEach(({ key, value }) => {
    if (!key.trim()) return;
    obj[key.trim()] = value === 'unlimited' ? 'unlimited' : Number(value) || 0;
  });
  return obj;
}

ProductFormDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  product: PropTypes.object,
  // Solo en modo creación: precarga el formulario (p. ej. al duplicar un producto).
  initialValues: PropTypes.object,
  apiProps: PropTypes.object.isRequired,
  onSaved: PropTypes.func,
};

export default function ProductFormDialog({ open, onClose, product, initialValues, apiProps, onSaved }) {
  const isEdit = Boolean(product);

  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const [catalog, setCatalog] = useState(null);
  const [catalogLoading, setCatalogLoading] = useState(false);

  useEffect(() => {
    if (!open) return;
    setCatalogLoading(true);
    authApiCall({
      method: 'GET',
      endpoint: endpoints.products,
      path: '/catalog',
      ...apiProps,
    })
      .then((res) => setCatalog(res?.data || null))
      .catch(() => setCatalog(null))
      .finally(() => setCatalogLoading(false));
  }, [open]);

  const catalogCapabilities = catalog?.capabilities || [];
  const catalogLimits = catalog?.limits || {};
  const catalogLimitKeys = Object.keys(catalogLimits);

  useEffect(() => {
    if (!open) return;
    setError(null);
    setSaving(false);
    const source = product || initialValues;
    if (source) {
      setForm({
        slug: source.slug || '',
        type: source.type || 'plan',
        displayName: source.displayName || '',
        status: source.status || 'active',
        capabilities: source.capabilities || [],
        limits: limitsToEntries(source.limits),
        comments: source.comments || '',
      });
    } else {
      setForm(EMPTY_FORM);
    }
  }, [open, product, initialValues]);

  const set = (field) => (e) => setForm((prev) => ({ ...prev, [field]: e.target.value }));

  const addLimit = (key) => {
    if (!key || form.limits.some((l) => l.key === key)) return;
    setForm((prev) => ({ ...prev, limits: [...prev.limits, { key, value: '' }] }));
  };

  const updateLimitValue = (index, value) => {
    setForm((prev) => ({
      ...prev,
      limits: prev.limits.map((l, i) => (i === index ? { ...l, value } : l)),
    }));
  };

  const toggleUnlimited = (index) => {
    setForm((prev) => ({
      ...prev,
      limits: prev.limits.map((l, i) =>
        i === index ? { ...l, value: l.value === 'unlimited' ? '' : 'unlimited' } : l
      ),
    }));
  };

  const removeLimit = (index) => {
    setForm((prev) => ({ ...prev, limits: prev.limits.filter((_, i) => i !== index) }));
  };

  const usedLimitKeys = new Set(form.limits.map((l) => l.key));
  const availableLimitKeys = catalogLimitKeys.filter((k) => !usedLimitKeys.has(k));

  const save = async () => {
    if (!form.slug.trim()) {
      setError('El slug es obligatorio.');
      return;
    }
    if (!form.displayName.trim()) {
      setError('El nombre visible es obligatorio.');
      return;
    }

    setSaving(true);
    setError(null);

    try {
      const body = {};

      if (isEdit) {
        if (form.displayName !== product.displayName) body.displayName = form.displayName;
        if (form.type !== product.type) body.type = form.type;
        if (form.status !== product.status) body.status = form.status;

        const caps = form.capabilities;
        const prevCaps = product.capabilities || [];
        if (JSON.stringify(caps) !== JSON.stringify(prevCaps)) body.capabilities = caps;

        const lim = entriesToLimits(form.limits);
        const prevLim = product.limits || {};
        if (JSON.stringify(lim) !== JSON.stringify(prevLim)) body.limits = lim;

        // La nota interna es patch: mandarla la guarda, mandar null la borra y omitir la clave
        // la deja como estaba. Vaciar el campo = borrar; nunca mandamos '' (perdería la nota
        // sin que se note). Solo se incluye si de verdad ha cambiado.
        const comments = form.comments.trim() ? form.comments.trim() : null;
        const prevComments = product.comments || null;
        if (comments !== prevComments) body.comments = comments;

        if (!Object.keys(body).length) {
          setError('No hay cambios que guardar.');
          setSaving(false);
          return;
        }

        await authApiCall({
          method: 'PUT',
          endpoint: endpoints.products,
          path: `/${product.slug}`,
          data: body,
          ...apiProps,
        });
      } else {
        body.slug = form.slug.trim();
        body.type = form.type;
        body.displayName = form.displayName.trim();
        body.status = form.status;
        if (form.capabilities.length) body.capabilities = form.capabilities;
        const lim = entriesToLimits(form.limits);
        if (Object.keys(lim).length) body.limits = lim;
        if (form.comments.trim()) body.comments = form.comments.trim();

        await authApiCall({
          method: 'POST',
          endpoint: endpoints.products,
          path: '/',
          data: body,
          ...apiProps,
        });
      }

      if (onSaved) onSaved();
    } catch (e) {
      const msg = e?.response?.data?.error || e?.message || 'Error al guardar el producto';
      setError(typeof msg === 'string' ? msg : JSON.stringify(msg));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={() => !saving && onClose()} maxWidth="sm" fullWidth>
      <DialogTitle>{isEdit ? 'Editar producto' : initialValues ? 'Duplicar producto' : 'Crear producto'}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          {isEdit && (
            <Alert severity="info" variant="outlined" icon={<Iconify icon="eva:info-outline" width={20} />}>
              Si necesitas un enfoque diferente, considera crear un producto nuevo en lugar de modificar este. Los
              cambios se aplican a todos los seats que usan este plan.
            </Alert>
          )}

          <TextField
            label="Slug"
            value={form.slug}
            onChange={set('slug')}
            disabled={isEdit}
            required
            size="small"
            fullWidth
            helperText={
              isEdit
                ? 'El slug no es modificable.'
                : 'Identificador único. Es el valor que hay que poner en la metadata del producto de Stripe para vincularlo.'
            }
          />

          <Stack direction="row" spacing={2}>
            <TextField label="Tipo" value={form.type} onChange={set('type')} select size="small" fullWidth>
              {TYPE_OPTIONS.map((o) => (
                <MenuItem key={o.value} value={o.value}>
                  {o.label}
                </MenuItem>
              ))}
            </TextField>

            <TextField label="Estado" value={form.status} onChange={set('status')} select size="small" fullWidth>
              {PLAN_STATUS_OPTIONS.map((o) => (
                <MenuItem key={o.value} value={o.value}>
                  {o.label}
                </MenuItem>
              ))}
            </TextField>
          </Stack>

          <TextField
            label="Nombre visible"
            value={form.displayName}
            onChange={set('displayName')}
            required
            size="small"
            fullWidth
          />

          {/* --- Capabilities --- */}
          <Box>
            <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
              Capabilities
            </Typography>
            <Autocomplete
              multiple
              options={catalogCapabilities}
              value={form.capabilities}
              onChange={(_, v) => setForm((prev) => ({ ...prev, capabilities: v }))}
              loading={catalogLoading}
              disableCloseOnSelect
              size="small"
              renderTags={(value, getTagProps) =>
                value.map((cap, index) => <Chip size="small" label={cap} {...getTagProps({ index })} key={cap} />)
              }
              renderInput={(params) => (
                <TextField {...params} placeholder={form.capabilities.length === 0 ? 'Selecciona capabilities' : ''} />
              )}
            />
          </Box>

          {/* --- Limits --- */}
          <Box>
            <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 0.5 }}>
              <Typography variant="subtitle2">Limits</Typography>
            </Stack>

            {availableLimitKeys.length > 0 && (
              <TextField
                select
                size="small"
                fullWidth
                value=""
                label="Añadir limit"
                onChange={(e) => addLimit(e.target.value)}
                sx={{ mb: 1 }}
              >
                {availableLimitKeys.map((k) => {
                  const meta = catalogLimits[k];
                  const hint = [
                    meta?.type,
                    meta?.unit,
                    meta?.allowUnlimited && 'unlimited',
                    meta?.depletes && 'depletes',
                  ]
                    .filter(Boolean)
                    .join(' · ');
                  return (
                    <MenuItem key={k} value={k}>
                      <Stack>
                        <Typography variant="body2">{k}</Typography>
                        {hint && (
                          <Typography variant="caption" color="text.secondary">
                            {hint}
                          </Typography>
                        )}
                      </Stack>
                    </MenuItem>
                  );
                })}
              </TextField>
            )}

            <Stack spacing={1}>
              {form.limits.map((l, i) => {
                const meta = catalogLimits[l.key] || {};
                const isUnlimited = l.value === 'unlimited';
                return (
                  <Stack key={l.key} direction="row" spacing={1} alignItems="center">
                    <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: 12, minWidth: 180 }}>
                      {l.key}
                      {meta.unit && (
                        <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 0.5 }}>
                          ({meta.unit})
                        </Typography>
                      )}
                    </Typography>
                    <TextField
                      size="small"
                      type="number"
                      value={isUnlimited ? '' : l.value}
                      onChange={(e) => updateLimitValue(i, e.target.value)}
                      disabled={isUnlimited}
                      placeholder={isUnlimited ? 'unlimited' : '0'}
                      sx={{ flex: 1 }}
                    />
                    {meta.allowUnlimited && (
                      <FormControlLabel
                        control={<Checkbox size="small" checked={isUnlimited} onChange={() => toggleUnlimited(i)} />}
                        label={<Typography variant="caption">Ilimitado</Typography>}
                        sx={{ mr: 0 }}
                      />
                    )}
                    <Iconify
                      icon="eva:close-circle-outline"
                      width={20}
                      sx={{
                        color: 'text.disabled',
                        cursor: 'pointer',
                        flexShrink: 0,
                        '&:hover': { color: 'error.main' },
                      }}
                      onClick={() => removeLimit(i)}
                    />
                  </Stack>
                );
              })}
              {form.limits.length === 0 && (
                <Typography variant="caption" color="text.disabled">
                  Sin límites definidos.
                </Typography>
              )}
            </Stack>
          </Box>

          {/* --- Nota interna --- */}
          <Box>
            <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
              Nota interna
            </Typography>
            <TextField
              value={form.comments}
              onChange={set('comments')}
              multiline
              minRows={2}
              maxRows={8}
              size="small"
              fullWidth
              placeholder="Por qué existe este plan, a qué sustituye, a quién no ofrecérselo..."
              helperText={
                isEdit && product.comments
                  ? 'Solo la ve el equipo interno. Si vacías el campo, la nota se borra al guardar.'
                  : 'Solo la ve el equipo interno. Nunca se muestra al usuario final.'
              }
            />
          </Box>

          {error && <Alert severity="error">{error}</Alert>}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>
          Cancelar
        </Button>
        <LoadingButton variant="contained" onClick={save} loading={saving}>
          {isEdit ? 'Guardar cambios' : 'Crear producto'}
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
}
